import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';

import {
  SR_HITLIST_STATUS_CREATED,
  SR_HITLIST_STATUS_STARTED,
  SR_HITLIST_STATUS_COMPLETED,
  SR_HITLIST_TYPE_HIGH_PRIORITY_LIST,
} from 'common/hitlistTaskConstants';

import {
  SR_API_ACTION_HITLIST_TASK_START,
  SR_API_ACTION_HITLIST_TASK_COMPLETE,
} from 'api/actionTypes';

import { makeHitlistTaskListId } from 'client/actions/HitlistTask/getList';
import HitlistTaskActions from 'client/actions/HitlistTask';

import useBranch from 'client/hooks/useBranch';
import postWithState from 'client/actions/postWithState';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import HitlistTaskListRow from 'client/components/HitlistTaskList/HitlistTaskListRow';

const HitlistTaskList = ({
  title,
  status,
  callOutcome,
  toCompletedAt,
  currentUser,
  dispatch,
  tourListingId,
}) => {
  const hitlistTaskListId = makeHitlistTaskListId({
    status,
    callOutcome,
    toCompletedAt,
    tourListingId,
  });

  const { hitlistTasks = [], hitlistCallScripts = {} } = useBranch({
    hitlistTasks: ['hitlistTask', 'list', hitlistTaskListId],
    hitlistCallScripts: ['hitlistTask', 'callScripts'],
  });

  const activeHitlistTask = hitlistTasks.find(
    (hitlistTask) =>
      hitlistTask.task.status === SR_HITLIST_STATUS_STARTED &&
      hitlistTask.assignedUser?.id === currentUser.id,
  );

  const getHitlistTasks = useCallback(() => {
    dispatch(HitlistTaskActions.getList, {
      status,
      callOutcome,
      toCompletedAt,
      tourListingId,
      refresh: true,
    });
  }, [dispatch, status, callOutcome, toCompletedAt, tourListingId]);

  const onButtonClick = ({ hitlistTask, cancelSameHouseholdTasks }) => {
    if (activeHitlistTask && activeHitlistTask.task.id !== hitlistTask.id) {
      return;
    }
    if (
      hitlistTask.status !== SR_HITLIST_STATUS_COMPLETED &&
      hitlistTask.assignedUserId &&
      hitlistTask.assignedUserId !== currentUser.id
    ) {
      return;
    }

    switch (hitlistTask.status) {
      case SR_HITLIST_STATUS_CREATED:
      case SR_HITLIST_STATUS_COMPLETED: {
        dispatch(postWithState, {
          action: SR_API_ACTION_HITLIST_TASK_START,
          payload: {
            assignedUserId: currentUser.id,
            hitlistTaskId: hitlistTask.id,
          },
          onSuccess: (hitlistTaskUpdated) => {
            dispatch(HitlistTaskActions.setList, {
              hitlistTaskListId,
              hitlistTaskList: hitlistTasks.map((hitlistTask) => {
                return hitlistTask.task.id === hitlistTaskUpdated.id
                  ? {
                      ...hitlistTask,
                      assignedUser: currentUser,
                      task: hitlistTaskUpdated,
                    }
                  : hitlistTask;
              }),
            });
          },
          onError: (error) => {
            global.alert(error.message);
            getHitlistTasks();
          },
        });
        break;
      }
      case SR_HITLIST_STATUS_STARTED: {
        dispatch(postWithState, {
          action: SR_API_ACTION_HITLIST_TASK_COMPLETE,
          payload: {
            hitlistTaskId: hitlistTask.id,
            cancelSameHouseholdTasks: cancelSameHouseholdTasks,
          },
          onSuccess: getHitlistTasks,
          onError: (error) => {
            global.alert(error.message);
          },
        });
        break;
      }
    }
  };

  const onUpdateHitlistTask = ({ hitlistTask, updates }) => {
    dispatch(HitlistTaskActions.update, {
      updates,
      hitlistTaskListId,
      hitlistTaskId: hitlistTask.id,
    }).catch((error) => {
      global.alert(error.message);
    });
  };

  useEffect(() => {
    getHitlistTasks();
    dispatch(HitlistTaskActions.getCallScripts);
  }, [getHitlistTasks, dispatch]);

  const filteredHitlistTasks = hitlistTasks.filter((hitlistTask) => {
    return (
      hitlistTask.listing.highPriority ||
      SR_HITLIST_TYPE_HIGH_PRIORITY_LIST.includes(hitlistTask.task.type)
    );
  });

  return (
    <div>
      <SrBox>
        <SrText font="greycliff" size="28" weight="800">
          {title}
        </SrText>
      </SrBox>
      <SrBox marginTop="40px">
        {filteredHitlistTasks.map((hitlistTask) => (
          <HitlistTaskListRow
            key={hitlistTask.task.id}
            hitlistTask={hitlistTask.task}
            renter={hitlistTask.renter}
            listing={hitlistTask.listing}
            tourListing={hitlistTask.tourListing}
            review={hitlistTask.review}
            partner={hitlistTask.partner}
            household={hitlistTask.household}
            rentlinxLead={hitlistTask.rentlinxLead}
            rentlinxListingLead={hitlistTask.rentlinxListingLead}
            currentUser={currentUser}
            assignedUser={hitlistTask.assignedUser}
            onButtonClick={onButtonClick}
            onUpdate={onUpdateHitlistTask}
            dispatch={dispatch}
            getHitlistTasks={getHitlistTasks}
            hitlistCallScripts={hitlistCallScripts}
            concession={hitlistTask.concession}
            frontConversation={hitlistTask.frontConversation}
            householdTasksCount={hitlistTask.householdTasksCount}
          />
        ))}
      </SrBox>
    </div>
  );
};

HitlistTaskList.propTypes = {
  title: PropTypes.string.isRequired,
  status: PropTypes.oneOfType([
    PropTypes.oneOf([
      SR_HITLIST_STATUS_CREATED,
      SR_HITLIST_STATUS_STARTED,
      SR_HITLIST_STATUS_COMPLETED,
    ]),
    PropTypes.array,
  ]).isRequired,
  callOutcome: PropTypes.string,
  toCompletedAt: PropTypes.string,
  tourListingId: PropTypes.number,
  currentUser: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

HitlistTaskList.displayName = 'HitlistTaskList';

export default HitlistTaskList;
